import React, { useEffect, useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { authClient } from "../lib/auth-client";
import { Box, CircularProgress, Typography } from '@mui/material';
import { ProtectedSessionProvider } from '../contexts/ProtectedSessionContext.jsx';

const LOGIN_GRACE_KEY = "auth_login_grace_until";
const LOGIN_GRACE_RETRY_INTERVAL_MS = 1500;

function getRemainingLoginGraceMs() {
  if (typeof window === "undefined") return 0;

  const rawValue = window.sessionStorage.getItem(LOGIN_GRACE_KEY);
  const expiresAt = rawValue ? Number(rawValue) : 0;

  if (!expiresAt || Number.isNaN(expiresAt)) {
    return 0;
  }

  return Math.max(0, expiresAt - Date.now());
}

function clearLoginGrace() {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(LOGIN_GRACE_KEY);
}

const ProtectedRoute = () => {
  const { data: session, isPending, refetch } = authClient.useSession();
  const location = useLocation();
  const remainingLoginGraceMs = useMemo(() => getRemainingLoginGraceMs(), []);
  const withinLoginGrace = !session && remainingLoginGraceMs > 0;

  useEffect(() => {
    if (session) {
      clearLoginGrace();
    }
  }, [session]);

  useEffect(() => {
    if (!withinLoginGrace) return undefined;

    let cancelled = false;

    const retrySession = async () => {
      if (cancelled) return;
      await refetch({
        fetchOptions: { force: true },
      });
    };

    void retrySession();
    const intervalId = window.setInterval(() => {
      void retrySession();
    }, LOGIN_GRACE_RETRY_INTERVAL_MS);

    const timeoutId = window.setTimeout(() => {
      window.clearInterval(intervalId);
    }, remainingLoginGraceMs);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [refetch, remainingLoginGraceMs, withinLoginGrace]);

  if ((isPending && !session) || withinLoginGrace) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh', 
        bgcolor: 'background.default',
        gap: 2,
        px: 3,
      }}>
        <CircularProgress sx={{ color: '#38bdf8' }} />
        {withinLoginGrace ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            Estamos terminando de iniciar tu sesion...
          </Typography>
        ) : null}
      </Box>
    );
  }

  if (!session) {
    // evitamos escupirlo al login instantaneamente, por si tiene problemas de red
    clearLoginGrace();
    return <Navigate to="/" replace />;
  }

  // If the user has no active membership at all and does not own any barbershop
  if (!session.barbershopId && !session.isOrganizationOwner) {
    return <Navigate to="/" replace />;
  }

  // Redirect inactive users to /sales if they try to access anything else
  const isSalesRoute = location.pathname.startsWith('/sales');
  if (session.isActive === false && !isSalesRoute) {
    return <Navigate to="/sales" replace />;
  }

  return (
    <ProtectedSessionProvider session={session}>
      <Outlet />
    </ProtectedSessionProvider>
  );
};

export default ProtectedRoute;
